import { computed, ref, useStore } from "@nuxtjs/composition-api";
import { defineStore } from "pinia";

import { useI18n } from "@/composables";

import useCreatorShopSearchApi from "./-api";
import { getLtksStore } from "../ltks";
import { getProductsStore } from "../products";

interface CreatorShopSearchStoreContext {
  $api: ReturnType<typeof useCreatorShopSearchApi>;
}

export const useCreatorShopSearchStore = defineStore(
  "creator-shop-search",
  () => {
    const store = useStore();
    const productsStore = getProductsStore(store);
    const ltksStore = getLtksStore(store);
    const i18n = useI18n();

    // State
    const ltksSearchFeedMap = ref<CreatorShopLtksFeedMap>({});
    const productsSearchFeedMap = ref<CreatorShopProductsFeedMap>({});

    const locale = computed(() => i18n.localeProperties.iso);
    const countryCode = computed(() => {
      const localeValue = locale.value;
      if (!localeValue) {
        return;
      }
      return new Intl.Locale(localeValue).region;
    });

    // Actions
    async function fetchLtkSearchResults(
      this: CreatorShopSearchStoreContext,
      profileId: string,
      data: PostCreatorShopSearchRequest,
    ) {
      const response = await this.$api.fetchCreatorShopSearchPostResults(profileId, {
        ...data,
        locale: locale.value,
        countryCode: countryCode.value,
      });
      await ltksStore.fetchLtksByIds(response.data.items.map((item) => item.id));
      ltksSearchFeedMap.value = Object.assign({}, ltksSearchFeedMap.value, { [data.query]: response.data });
    }

    async function fetchMoreLtkSearchResults(
      this: CreatorShopSearchStoreContext,
      profileId: string,
      data: PostCreatorShopSearchRequest,
    ) {
      const response = await this.$api.fetchCreatorShopSearchPostResults(profileId, {
        ...data,
        locale: locale.value,
        countryCode: countryCode.value,
      });
      await ltksStore.fetchLtksByIds(response.data.items.map((item) => item.id));
      const existingFeed = ltksSearchFeedMap.value[data.query];
      ltksSearchFeedMap.value = Object.assign({}, ltksSearchFeedMap.value, {
        [data.query]: { ...response.data, items: [...(existingFeed?.items || []), ...response.data.items] },
      });
    }

    async function fetchProductSearchResults(
      this: CreatorShopSearchStoreContext,
      profileId: string,
      data: Omit<PostCreatorShopSearchRequest, "types">,
    ) {
      const response = await this.$api.fetchCreatorShopSearchProductResults(profileId, {
        ...data,
        locale: locale.value,
        countryCode: countryCode.value,
      });
      await productsStore.fetchProductsByIds({
        ids: response.data.items.map((item) => item.id),
        fetchNestedModels: true,
      });
      productsSearchFeedMap.value = Object.assign({}, productsSearchFeedMap.value, { [data.query]: response.data });
    }

    async function fetchMoreProductSearchResults(
      this: CreatorShopSearchStoreContext,
      profileId: string,
      data: Omit<PostCreatorShopSearchRequest, "types">,
    ) {
      const response = await this.$api.fetchCreatorShopSearchProductResults(profileId, {
        ...data,
        locale: locale.value,
        countryCode: countryCode.value,
      });
      await productsStore.fetchProductsByIds({
        ids: response.data.items.map((item) => item.id),
        fetchNestedModels: true,
      });
      const existingFeed = productsSearchFeedMap.value[data.query];
      productsSearchFeedMap.value = Object.assign({}, productsSearchFeedMap.value, {
        [data.query]: { ...response.data, items: [...(existingFeed?.items || []), ...response.data.items] },
      });
    }

    function resetStore() {
      ltksSearchFeedMap.value = {};
      productsSearchFeedMap.value = {};
    }

    function resetSearch() {
      ltksSearchFeedMap.value = {};
      productsSearchFeedMap.value = {};
    }

    return {
      ltksSearchFeedMap,
      productsSearchFeedMap,
      fetchLtkSearchResults,
      fetchMoreLtkSearchResults,
      fetchProductSearchResults,
      fetchMoreProductSearchResults,
      resetStore,
      resetSearch,
    };
  },
  {
    api: useCreatorShopSearchApi,
  },
);
